import {useCallback, useState} from 'react'
import {Box, Button, IconButton, useResponsiveValue} from '@primer/react'
import {CodeIcon, StackIcon, XIcon} from '@primer/octicons-react'
import type {GettingStarted, ModelInputSchema, SidebarSelectionOptions} from '../../../types'
import type {Model} from '@github-ui/marketplace-common'
import {GettingStartedButtonClicked, templateRepositoryNwo} from '../../../utils/playground-types'
import {ModelUrlHelper} from '../../../utils/model-url-helper'
import ModelSwitcher from './ModelSwitcher'
import GettingStartedDialog from './GettingStartedDialog/GettingStartedDialog'
import {PresetsMenu} from './presets/PresetsMenu'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Panel} from '../../../utils/playground-manager'
import {usePlaygroundState} from '../../../contexts/PlaygroundStateContext'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {GiveFeedback} from '../../../components/GiveFeedback'
import type {ModelPersistentUIState} from '../../../utils/playground-local-storage'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'

export function PlaygroundHeader({
  model,
  modelInputSchema,
  canUseO1Models,
  position,
  gettingStarted,
  uiState,
  setUiState,
  handleSetSidebarTab,
}: {
  model: Model
  modelInputSchema: ModelInputSchema
  canUseO1Models: boolean
  position: number
  gettingStarted: GettingStarted
  uiState: ModelPersistentUIState
  setUiState: (uiState: ModelPersistentUIState) => void
  handleSetSidebarTab: (newTab: SidebarSelectionOptions) => void
}) {
  const openInCodespacesUrl = `/codespaces/new?template_repository=${templateRepositoryNwo}`
  const [isGettingStartedDialogOpen, setGettingStartedDialogOpen] = useState(false)

  const {models = []} = usePlaygroundState()
  const onComparisonMode = models.length > 1
  const isMobile = useResponsiveValue({narrow: true}, false)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const featureFlags = useFeatureFlags()

  const comparisonStyle = {
    p: 2,
    pb: 2,
    borderColor: 'border.default',
    borderWidth: 1,
    borderStyle: 'solid',
    borderTopRightRadius: 2,
    borderTopLeftRadius: 2,
  }

  const individualStyle = {
    p: 0,
    pb: 3,
    borderColor: null,
    borderWidth: 0,
    borderStyle: null,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
  }

  const onCloseModel = () => {
    if (position === Panel.Main) {
      // If we're removing the main model, we need to navigate to the new main model
      navigate({
        pathname: ModelUrlHelper.playgroundUrl(models[Panel.Side]?.catalogData || model),
        search: `retain=${Panel.Side}`,
      })
    } else {
      // If we're removing the side model, we just remove the compare_to
      setSearchParams()
    }
  }

  const onModelSelect = useCallback(
    async (m: Model) => {
      if (position === Panel.Side) {
        setSearchParams({
          compare_to: m.name,
        })
      } else {
        navigate({
          pathname: ModelUrlHelper.playgroundUrl(m),
          search: searchParams.toString(),
        })
      }
    },
    [position, setSearchParams, navigate, searchParams],
  )

  const handleAddModel = async (m: Model) => {
    const modelName = m.name
    if (!modelName) return

    setSearchParams({
      compare_to: modelName,
    })
  }

  return (
    <Box
      sx={{
        ...(onComparisonMode ? comparisonStyle : individualStyle),
        display: 'flex',
        gap: 3,
        flexDirection: 'column',
        borderBottomWidth: 0,
      }}
    >
      <div>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <div className="d-flex flex-items-center gap-2">
            <ModelSwitcher
              model={model}
              canUseO1Models={canUseO1Models}
              onSelect={onModelSelect}
              onComparisonMode={onComparisonMode}
              handleSetSidebarTab={handleSetSidebarTab}
            />
            {!onComparisonMode && !isMobile && (
              <ModelSwitcher
                model={model}
                canUseO1Models={canUseO1Models}
                onSelect={handleAddModel}
                onComparisonMode={false}
                handleSetSidebarTab={handleSetSidebarTab}
                variant="compare_button"
              />
            )}
          </div>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              width: 'auto',
              justifyContent: 'flex-end',
              flexGrow: 1,
              flexShrink: 0,
              flexDirection: 'row',
            }}
          >
            <Box sx={{display: ['none', 'none', 'flex']}}>{!onComparisonMode && <GiveFeedback />}</Box>

            {!onComparisonMode && featureFlags.github_models_prompt_evals && (
              <>
                <IconButton
                  sx={{display: ['flex', 'none', 'none']}}
                  icon={StackIcon}
                  aria-label="Prompt playground"
                  onClick={() => {
                    navigate({
                      pathname: ModelUrlHelper.promptUrl(model),
                    })
                  }}
                />
                <Button
                  sx={{display: ['none', 'flex', 'flex']}}
                  leadingVisual={StackIcon}
                  onClick={() => {
                    navigate({
                      pathname: ModelUrlHelper.promptUrl(model),
                    })
                  }}
                >
                  Prompt editor
                </Button>
              </>
            )}

            {!onComparisonMode && (
              <Box sx={{display: ['none', 'none', 'flex']}}>
                <PresetsMenu
                  modelDetails={{
                    catalogData: model,
                    modelInputSchema,
                    gettingStarted,
                  }}
                />
              </Box>
            )}

            <>
              <IconButton
                sx={{display: ['flex', 'none', 'none']}}
                icon={CodeIcon}
                variant="primary"
                aria-label="Get API key"
                onClick={() => {
                  setGettingStartedDialogOpen(true)
                  sendEvent(GettingStartedButtonClicked, {
                    registry: model.registry,
                    model: model.name,
                    publisher: model.publisher,
                  })
                }}
              />
              <Button
                sx={{display: ['none', 'flex', 'flex']}}
                leadingVisual={CodeIcon}
                variant="primary"
                onClick={() => {
                  setGettingStartedDialogOpen(true)
                  sendEvent(GettingStartedButtonClicked, {
                    registry: model.registry,
                    model: model.name,
                    publisher: model.publisher,
                  })
                }}
              >
                Get API key
              </Button>
              {onComparisonMode && <IconButton icon={XIcon} aria-label="Close model" onClick={onCloseModel} />}
            </>
            {isGettingStartedDialogOpen && (
              <GettingStartedDialog
                openInCodespaceUrl={openInCodespacesUrl}
                onClose={() => setGettingStartedDialogOpen(false)}
                showCodespacesSuggestion
                gettingStarted={gettingStarted}
                modelName={model.name}
                uiState={uiState}
                setUiState={setUiState}
              />
            )}
          </Box>
        </Box>
      </div>
    </Box>
  )
}

try{ PlaygroundHeader.displayName ||= 'PlaygroundHeader' } catch {}